import { useState, useCallback, FormEvent } from 'react';
import {
  HsmContact,
  HsmContactBatch,
  HsmCsvImportResult
} from '../types';
import {
  fetchBatches as fetchBatchesApi,
  createBatch as createBatchApi,
  updateBatch as updateBatchApi,
  deleteBatch as deleteBatchApi,
  updateContactBatch as updateContactBatchApi,
  importContactsCsv as importContactsCsvApi
} from '../services/hsm.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseHsmBatchesProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageHsm: boolean;
  fetchContacts: () => Promise<{ success: boolean; error?: string }>;
}

export interface UseHsmBatchesReturn {
  // Batch state
  batches: HsmContactBatch[];
  batchesLoading: boolean;
  selectedBatchFilter: number | null;
  setSelectedBatchFilter: (batchId: number | null) => void;
  batchDialogOpen: boolean;
  setBatchDialogOpen: (open: boolean) => void;
  batchName: string;
  setBatchName: (value: string) => void;
  batchDescription: string;
  setBatchDescription: (value: string) => void;
  batchSaving: boolean;

  // Assign batch state
  assignBatchDialogOpen: boolean;
  setAssignBatchDialogOpen: (open: boolean) => void;
  contactToAssign: HsmContact | null;
  assignBatchId: string;
  setAssignBatchId: (value: string) => void;
  assigningBatch: boolean;

  // Contact batch ID for create/edit forms
  contactBatchId: string;
  setContactBatchId: (value: string) => void;

  // Batch edit state
  batchEditDialogOpen: boolean;
  setBatchEditDialogOpen: (open: boolean) => void;
  batchToEdit: HsmContactBatch | null;

  // Handlers
  fetchBatches: () => Promise<{ success: boolean; error?: string }>;
  handleCreateBatch: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDeleteBatch: (batchId: number) => Promise<void>;
  openAssignBatchDialog: (contact: HsmContact) => void;
  handleAssignBatch: () => Promise<void>;
  openBatchEditDialog: (batch: HsmContactBatch) => void;
  handleUpdateBatch: (batchId: number, name: string, description?: string) => Promise<{ success: boolean; error?: string }>;
  handleCsvImport: (file: File, batchId?: number, batchName?: string) => Promise<{ success: boolean; error?: string; result?: HsmCsvImportResult }>;
}

export function useHsmBatches({
  accessToken,
  attemptRefresh,
  canManageHsm,
  fetchContacts
}: UseHsmBatchesProps): UseHsmBatchesReturn {
  // Batch state
  const [batches, setBatches] = useState<HsmContactBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState<number | null>(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [batchDescription, setBatchDescription] = useState('');
  const [batchSaving, setBatchSaving] = useState(false);

  // Assign batch state
  const [assignBatchDialogOpen, setAssignBatchDialogOpen] = useState(false);
  const [contactToAssign, setContactToAssign] = useState<HsmContact | null>(null);
  const [assignBatchId, setAssignBatchId] = useState('');
  const [assigningBatch, setAssigningBatch] = useState(false);

  // Contact batch ID for forms
  const [contactBatchId, setContactBatchId] = useState('');

  // Batch edit state
  const [batchEditDialogOpen, setBatchEditDialogOpen] = useState(false);
  const [batchToEdit, setBatchToEdit] = useState<HsmContactBatch | null>(null);

  // Fetch batches
  const fetchBatches = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    setBatchesLoading(true);
    try {
      const data = await withAuthRetry(
        () => fetchBatchesApi(accessToken!),
        attemptRefresh
      );
      setBatches(data);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar lotes';
      return { success: false, error: errorMessage };
    } finally {
      setBatchesLoading(false);
    }
  }, [accessToken, attemptRefresh, canManageHsm]);

  // Create batch
  const handleCreateBatch = useCallback(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!canManageHsm || !accessToken || !batchName.trim()) {
      return;
    }

    setBatchSaving(true);
    try {
      await withAuthRetry(
        () => createBatchApi(
          { name: batchName.trim(), description: batchDescription.trim() || undefined },
          accessToken!
        ),
        attemptRefresh
      );
      setBatchDialogOpen(false);
      setBatchName('');
      setBatchDescription('');
      await fetchBatches();
    } catch (error) {
      console.error('Error creating batch:', error instanceof Error ? error.message : 'Erro ao criar lote');
    } finally {
      setBatchSaving(false);
    }
  }, [accessToken, attemptRefresh, canManageHsm, batchName, batchDescription, fetchBatches]);

  // Delete batch
  const handleDeleteBatch = useCallback(async (batchId: number): Promise<void> => {
    if (!canManageHsm || !accessToken) {
      return;
    }

    try {
      await withAuthRetry(
        () => deleteBatchApi(batchId, accessToken!),
        attemptRefresh
      );
      await fetchBatches();
      // Clear filter if we deleted the currently filtered batch
      if (selectedBatchFilter === batchId) {
        setSelectedBatchFilter(null);
      }
    } catch (error) {
      console.error('Error deleting batch:', error instanceof Error ? error.message : 'Erro ao excluir lote');
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchBatches, selectedBatchFilter]);

  // Open assign batch dialog
  const openAssignBatchDialog = useCallback((contact: HsmContact) => {
    setContactToAssign(contact);
    setAssignBatchId(contact.batchId?.toString() || '');
    setAssignBatchDialogOpen(true);
  }, []);

  // Assign batch to contact
  const handleAssignBatch = useCallback(async (): Promise<void> => {
    if (!canManageHsm || !accessToken || !contactToAssign) {
      return;
    }

    setAssigningBatch(true);
    try {
      const newBatchId = assignBatchId ? parseInt(assignBatchId, 10) : null;
      await withAuthRetry(
        () => updateContactBatchApi(contactToAssign.id, { batchId: newBatchId }, accessToken!),
        attemptRefresh
      );
      setAssignBatchDialogOpen(false);
      setContactToAssign(null);
      setAssignBatchId('');
      await fetchContacts();
    } catch (error) {
      console.error('Error assigning batch:', error instanceof Error ? error.message : 'Erro ao atribuir lote');
    } finally {
      setAssigningBatch(false);
    }
  }, [accessToken, attemptRefresh, canManageHsm, contactToAssign, assignBatchId, fetchContacts]);

  // Open batch edit dialog
  const openBatchEditDialog = useCallback((batch: HsmContactBatch) => {
    setBatchToEdit(batch);
    setBatchEditDialogOpen(true);
  }, []);

  // Update batch (rename)
  const handleUpdateBatch = useCallback(async (
    batchId: number,
    name: string,
    description?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => updateBatchApi(batchId, { name, description }, accessToken!),
        attemptRefresh
      );
      setBatchEditDialogOpen(false);
      setBatchToEdit(null);
      await fetchBatches();
      await fetchContacts(); // Refresh contacts to update batch names
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar lote' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchBatches, fetchContacts]);

  // CSV Import
  const handleCsvImport = useCallback(async (
    file: File,
    batchId?: number,
    batchName?: string
  ): Promise<{ success: boolean; error?: string; result?: HsmCsvImportResult }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      const result = await withAuthRetry(
        () => importContactsCsvApi(file, accessToken!, { batchId, batchName }),
        attemptRefresh
      );
      await fetchBatches();
      await fetchContacts();
      return { success: true, result };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao importar CSV' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchBatches, fetchContacts]);

  return {
    batches,
    batchesLoading,
    selectedBatchFilter,
    setSelectedBatchFilter,
    batchDialogOpen,
    setBatchDialogOpen,
    batchName,
    setBatchName,
    batchDescription,
    setBatchDescription,
    batchSaving,
    assignBatchDialogOpen,
    setAssignBatchDialogOpen,
    contactToAssign,
    assignBatchId,
    setAssignBatchId,
    assigningBatch,
    contactBatchId,
    setContactBatchId,
    batchEditDialogOpen,
    setBatchEditDialogOpen,
    batchToEdit,
    fetchBatches,
    handleCreateBatch,
    handleDeleteBatch,
    openAssignBatchDialog,
    handleAssignBatch,
    openBatchEditDialog,
    handleUpdateBatch,
    handleCsvImport
  };
}
